import { Skeleton, Tooltip } from '@mantine/core'
import { Info, Minus, TrendingDown, TrendingUp } from 'lucide-react'

export type HeroStatDeltaDirection = 'up' | 'down' | 'neutral'

export interface HeroStatDelta {
  direction: HeroStatDeltaDirection
  label: string
  tooltip?: string
}

export interface HeroStatCardProps {
  label: string
  value: string | number
  suffix?: string
  /** Small muted line rendered under the value (e.g. "20 of 25 calls off-hours"). */
  caption?: string
  info?: string
  loading?: boolean
  /** When true, the loading state reserves a delta-sized skeleton next to the value
   * so the layout doesn't jump once data arrives. Set this on cards that you know
   * will render a delta after loading. */
  loadingShowDelta?: boolean
  delta?: HeroStatDelta
  featured?: boolean
  onClick?: () => void
}

const DELTA_COLOR_CLASS: Record<HeroStatDeltaDirection, string> = {
  up: 'text-doubleNickel-success-600',
  down: 'text-doubleNickel-error-600',
  neutral: 'text-doubleNickel-gray-500',
}

const DeltaIcon = ({ direction }: { direction: HeroStatDeltaDirection }) => {
  if (direction === 'up') return <TrendingUp size={12} strokeWidth={2.25} />
  if (direction === 'down') return <TrendingDown size={12} strokeWidth={2.25} />
  return <Minus size={12} strokeWidth={2.25} />
}

export const HeroStatCard = ({
  label,
  value,
  suffix,
  caption,
  info,
  loading = false,
  loadingShowDelta = false,
  delta,
  featured = false,
  onClick,
}: HeroStatCardProps) => {
  const clickable = typeof onClick === 'function'

  const containerClasses = [
    'rounded-lg border px-5 py-4 transition-all',
    featured
      ? 'border-doubleNickel-brand-100 bg-gradient-to-br from-white to-doubleNickel-blue-50'
      : 'border-gray-200 bg-white',
    clickable
      ? 'cursor-pointer hover:-translate-y-px hover:border-gray-300 hover:shadow-md'
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  const handleKeyDown = clickable
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }
    : undefined

  return (
    <div
      className={containerClasses}
      onClick={clickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-center gap-1.5">
        <p className="text-sm text-doubleNickel-gray-600">{label}</p>
        {info && (
          <Tooltip
            label={
              <span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                {info}
              </span>
            }
            multiline
            w={280}
            withArrow
            position="top"
          >
            <Info
              size={13}
              className="cursor-help text-gray-400"
              data-testid="hero-info-icon"
            />
          </Tooltip>
        )}
      </div>

      <div className="mt-1 flex items-baseline justify-between gap-2">
        {loading ? (
          <Skeleton height={28} width={80} radius="sm" />
        ) : (
          <p className="text-2xl font-semibold text-doubleNickel-gray-800">
            {value}
            {suffix && (
              <span className="ml-1 text-base font-medium text-gray-400">
                {suffix}
              </span>
            )}
          </p>
        )}

        {loading && loadingShowDelta && (
          <Skeleton
            height={14}
            width={56}
            radius="sm"
            data-testid="hero-delta-skeleton"
          />
        )}

        {!loading && delta && (
          <Tooltip
            label={
              <span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                {delta.tooltip ?? delta.label}
              </span>
            }
            multiline
            w={220}
            withArrow
            position="top"
            disabled={!delta.tooltip}
          >
            <span
              className={`inline-flex items-center gap-1 font-mono text-xs font-medium ${
                DELTA_COLOR_CLASS[delta.direction]
              }`}
              data-testid="hero-delta"
              data-direction={delta.direction}
            >
              <DeltaIcon direction={delta.direction} />
              {delta.label}
            </span>
          </Tooltip>
        )}
      </div>

      {!loading && caption && (
        <p
          className="mt-0.5 text-xs text-doubleNickel-gray-500"
          data-testid="hero-caption"
        >
          {caption}
        </p>
      )}
    </div>
  )
}
